import { EmailOtpPurpose } from '@dermloop/static-values';
import { ANALYTICS_EVENT_CATALOGUE } from './analytics/analytics-catalogue';
import {
  IsDateString,
  IsEmail,
  IsIn,
  IsNotEmpty,
  IsOptional,
  IsString,
  IsUrl,
  IsUUID,
  Matches,
  MaxLength,
  MinLength,
} from 'class-validator';

/**
 * DTO for searching medical units within Keycloak M2M context.
 */
export class KeycloakM2MSearchMedicalUnitsDto {
  /**
   * Search query used for:
   * - sorId
   * - providerNumber
   * - cvr
   */
  @IsString()
  @IsNotEmpty()
  searchQuery: string;
}

export interface KeycloakM2MSearchMedicalUnitsResultDto {
  federationId: string;
  federationName: string;
  jurisdictionalGroupId: string;
  jurisdictionalGroupName: string;
  medicalUnitId: string;
  medicalUnitName: string;
  sorId?: string;
  providerNumber?: string;
  cvr?: string;
}

/**
 * DTO for searching authorization codes within Keycloak M2M context.
 */
export class KeycloakM2MLookupAuthorizationCodeDto {
  @IsString()
  @MaxLength(10)
  @MinLength(10)
  cpr: string;
}

export interface KeycloakM2MLookupAuthorizationCodeResultDto {
  authorizationCode: string | null;
}

/**
 * DTO for searching for users in a medical unit with authorization codes
 * within Keycloak M2M context.
 */
export class KeycloakM2MSearchUsersDto {
  @IsUUID()
  medicalUnitId: string;
}

export interface KeycloakM2MSearchUsersResultDto {
  userId: string;
  userSub: string;
  firstName: string;
  lastName: string;
  authorizationCode: string;
}

/**
 * DTO for starting a passwordless (email code) sign-in within Keycloak M2M
 * context.
 */
export class KeycloakM2MPasswordlessStartDto {
  @IsEmail()
  email: string;

  /**
   * Keycloak-minted action-token URL, embedded in the sign-in email alongside
   * the code as the "same device" shortcut.
   */
  @IsOptional()
  @IsUrl({ require_protocol: true, require_tld: false })
  magicLink?: string;
}

/**
 * `otpId` is always present. An unknown email gets a random, non-persisted id
 * that simply fails to verify, so the response shape cannot be used to probe
 * which addresses have accounts.
 */
export interface KeycloakM2MPasswordlessStartResultDto {
  otpId: string;
}

/**
 * DTO for redeeming a passwordless / recovery code within Keycloak M2M context.
 */
export class KeycloakM2MPasswordlessVerifyDto {
  @IsUUID()
  otpId: string;

  @IsString()
  @IsNotEmpty()
  code: string;

  /**
   * Which flow is redeeming the code. Required: the caller states the authority
   * it expects and the API enforces it, so a login code can never be redeemed to
   * reset a password.
   *
   * Deliberately narrower than `EmailOtpPurpose`. The profile email-verification
   * code is minted on an authenticated, lower-bar path, so accepting it here
   * would let it be upgraded into a login or password-reset credential.
   */
  @IsIn([EmailOtpPurpose.Login, EmailOtpPurpose.Recovery])
  purpose: EmailOtpPurpose.Login | EmailOtpPurpose.Recovery;
}

/** `kratosId` is the identity anchor the issued Keycloak token must carry. */
export interface KeycloakM2MPasswordlessVerifyResultDto {
  verified: boolean;
  kratosId?: string;
}

/**
 * DTO for starting a password recovery (forgot-password) within Keycloak M2M
 * context.
 */
export class KeycloakM2MRecoveryStartDto {
  @IsEmail()
  email: string;
}

/**
 * DTO for the password-changed security notification within Keycloak M2M
 * context.
 */
export class KeycloakM2MPasswordChangedDto {
  /** The user's server-set `kratos_id` anchor, from the Keycloak user attribute. */
  @IsString()
  @IsNotEmpty()
  kratosId: string;
}

// Mirrored by the LoginMethod enum in the Keycloak SPI.
const LOGIN_METHODS =
  ANALYTICS_EVENT_CATALOGUE.login_method_selected.props.method.values;

export class KeycloakM2MLoginMethodSelectedDto {
  /** The app's `dermloop_analytics` authorize param, forwarded unparsed: the app chose it. */
  @IsString()
  @IsNotEmpty()
  // Mirrored by PasswordlessConstants.MAX_ANALYTICS_CONTEXT_LENGTH in the Keycloak SPI.
  @MaxLength(1000)
  analyticsContext: string;

  @IsIn(LOGIN_METHODS)
  method: (typeof LOGIN_METHODS)[number];

  /** When the user clicked: Keycloak may send the report much later. */
  @IsDateString({ strict: true })
  // A UTC instant only: a bare date or a zoneless time would parse in the api's own zone.
  @Matches(/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(\.\d+)?Z$/)
  selectedAt: string;
}
